import { useCallback, useState } from 'react';
import {
  AppearanceSetting,
  getAppearanceName,
  getSongIntroName,
  localStorageGet,
  LocalStorageKey,
  localStorageSet,
  SongIntroduction,
} from '../../data/client_local_storage.ts';
import { useIsAutoPlayNextSongEnabled } from '../../data/use_is_auto_play_next_song_enabled.ts';
import { useLocalStorage } from '../../data/use_local_storage.tsx';
import { isScreenWakeLockSupported } from '../../util/screen_wake_lock.ts';
import { applyTheme } from '../../util/startup.ts';
import '../settings_page.css';
import { SettingAccordion, SettingOption, SettingToggle } from './setting_rows.tsx';
import './shared.css';

export const songIntroductionSettingId = 'song-introduction-setting';
export const showPdfOnPlaySettingId = 'show-pdf-on-play-setting';
export const keepScreenAwakeSettingId = 'keep-screen-awake-setting';

// The settings whose option lists expand in place
export enum SongOptionsSetting {
  SongIntroduction = 'SongIntroduction',
  VerseCount = 'VerseCount',
  Appearance = 'Appearance',
  WebAudio = 'WebAudio',
  KeepScreenAwake = 'KeepScreenAwake',
}

export function getSongIntroductionOptionId(optionValue?: SongIntroduction): string {
  return `song-introduction-option-${optionValue ?? 'none'}`;
}

const songIntroductionOptions: SettingOption<SongIntroduction | undefined>[] = [
  undefined,
  ...Object.values(SongIntroduction),
].map((value) => ({
  label: getSongIntroName(value),
  value,
  id: getSongIntroductionOptionId(value),
}));

const verseCountOptions: SettingOption<number | undefined>[] = [
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: 'All', value: undefined },
];

const appearanceOptions: SettingOption<AppearanceSetting>[] = Object.values(AppearanceSetting).map((value) => ({
  label: getAppearanceName(value),
  value,
}));

const songIntroductionExplanation =
  'Song Introduction determines whether part or all of the song is played prior to the first verse. Begins playing ' +
  'at the measure nearest to the specified duration.';
const verseCountExplanation =
  'Verse Count determines how many times a song plays through, before stopping, repeating, or going to the next ' +
  "song. Setting the verse count to 'All' will make the song play through as many verses as are in a given song. " +
  'If we do not have this information for a song, it will default to 4 verses.';
const appearanceExplanation = "Shows the music with light or dark colors. Auto follows your device's setting.";

const yesNoOptions: SettingOption<boolean>[] = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];
const webAudioExplanation =
  'Tempo and volume changes take effect without a pause. Turn it off if playback misbehaves on this device.';
const keepScreenAwakeExplanation = 'Keeps the screen from dimming or locking while the song player is open.';

interface Props {
  initiallyExpandedSetting?: SongOptionsSetting;
}

export const SongOptions = ({ initiallyExpandedSetting }: Props) => {
  const [appearance, setAppearance] = useLocalStorage(LocalStorageKey.Appearance);
  const [verseCount, setVerseCount] = useLocalStorage(LocalStorageKey.VerseCount);
  const [songIntroduction, setSongIntroduction] = useLocalStorage(LocalStorageKey.SongIntroduction);
  const [webAudioPlayback, setWebAudioPlayback] = useLocalStorage(LocalStorageKey.WebAudioPlayback);
  const [keepScreenAwake, setKeepScreenAwake] = useLocalStorage(LocalStorageKey.KeepScreenAwake);
  const isAutoPlayNextSongEnabled = useIsAutoPlayNextSongEnabled();
  const [pdfAutoDisplay] = useLocalStorage(LocalStorageKey.PdfAutoDisplay);
  const [expandedSetting, setExpandedSetting] = useState(initiallyExpandedSetting);

  // Only one accordion is open at a time; tapping the open row collapses it
  const toggleExpanded = useCallback((setting: SongOptionsSetting) => {
    setExpandedSetting((expanded) => (expanded === setting ? undefined : setting));
  }, []);

  const handlePdfAutoDisplayToggle = useCallback(() => {
    localStorageSet(LocalStorageKey.PdfAutoDisplay, !localStorageGet(LocalStorageKey.PdfAutoDisplay));
  }, []);

  const handleAutoPlayNextSongToggle = useCallback(() => {
    localStorageSet(LocalStorageKey.AutoPlayNextSong, !isAutoPlayNextSongEnabled);
  }, [isAutoPlayNextSongEnabled]);

  const onSelectSongIntroduction = useCallback(
    (value: SongIntroduction | undefined) => {
      setSongIntroduction(value);
      setExpandedSetting(undefined);
    },
    [setSongIntroduction],
  );

  const onSelectVerseCount = useCallback(
    (value: number | undefined) => {
      setVerseCount(value);
      setExpandedSetting(undefined);
    },
    [setVerseCount],
  );

  const onSelectAppearance = useCallback(
    (value: AppearanceSetting) => {
      setAppearance(value);
      applyTheme();
      setExpandedSetting(undefined);
    },
    [setAppearance],
  );

  const onSelectWebAudio = useCallback(
    (value: boolean) => {
      setWebAudioPlayback(value);
      setExpandedSetting(undefined);
    },
    [setWebAudioPlayback],
  );

  const onSelectKeepScreenAwake = useCallback(
    (value: boolean) => {
      setKeepScreenAwake(value);
      setExpandedSetting(undefined);
    },
    [setKeepScreenAwake],
  );

  // Verse counts other than 1 or 2 display as 'All', matching the old picker's checkmark logic
  const verseCountValue = verseCount === 1 || verseCount === 2 ? verseCount : undefined;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', marginBottom: 35, whiteSpace: 'nowrap' }}>
      <SettingAccordion
        label="Song Introduction"
        id={songIntroductionSettingId}
        options={songIntroductionOptions}
        selectedValue={songIntroduction}
        onSelect={onSelectSongIntroduction}
        expanded={expandedSetting === SongOptionsSetting.SongIntroduction}
        onToggle={() => toggleExpanded(SongOptionsSetting.SongIntroduction)}
        explanation={songIntroductionExplanation}
      />
      <SettingToggle
        label="Show PDF on Play"
        id={showPdfOnPlaySettingId}
        checked={pdfAutoDisplay}
        onToggle={handlePdfAutoDisplayToggle}
      />
      <SettingToggle
        label="Autoplay Next Song"
        checked={isAutoPlayNextSongEnabled}
        onToggle={handleAutoPlayNextSongToggle}
      />
      <SettingAccordion
        label="Verse Count"
        options={verseCountOptions}
        selectedValue={verseCountValue}
        onSelect={onSelectVerseCount}
        expanded={expandedSetting === SongOptionsSetting.VerseCount}
        onToggle={() => toggleExpanded(SongOptionsSetting.VerseCount)}
        explanation={verseCountExplanation}
      />
      <SettingAccordion
        label="Music Appearance"
        options={appearanceOptions}
        selectedValue={appearance}
        onSelect={onSelectAppearance}
        expanded={expandedSetting === SongOptionsSetting.Appearance}
        onToggle={() => toggleExpanded(SongOptionsSetting.Appearance)}
        explanation={appearanceExplanation}
      />
      <SettingAccordion
        label="Web Audio"
        options={yesNoOptions}
        selectedValue={webAudioPlayback}
        onSelect={onSelectWebAudio}
        expanded={expandedSetting === SongOptionsSetting.WebAudio}
        onToggle={() => toggleExpanded(SongOptionsSetting.WebAudio)}
        explanation={webAudioExplanation}
      />
      {isScreenWakeLockSupported() && (
        <SettingAccordion
          label="Keep Screen Awake"
          id={keepScreenAwakeSettingId}
          options={yesNoOptions}
          selectedValue={keepScreenAwake}
          onSelect={onSelectKeepScreenAwake}
          expanded={expandedSetting === SongOptionsSetting.KeepScreenAwake}
          onToggle={() => toggleExpanded(SongOptionsSetting.KeepScreenAwake)}
          explanation={keepScreenAwakeExplanation}
        />
      )}
    </div>
  );
};
